import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Skeleton from "@mui/material/Skeleton"
import ImageIcon from '@mui/icons-material/Image'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import LastEventChip from './components/LastEventDisplay/LastEventChip'
import {
  useGetLastEntriesByChannelQuery,
  useGetPhotoNumberQuery
} from '@/api/enterance'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import LastEventZoomInModal from './components/LastEventDisplay/LastEventZoomInModal'
import type { ChannelCardDragHandlers } from './useChannelCardDrag'

interface LastEventDisplayProps {
  channelName?: string
  channelId: number | string
  dragHandleProps?: ChannelCardDragHandlers
}

// TODO Add vehicle icons
const LastEventDisplay = ({
  channelName,
  channelId,
  dragHandleProps,
}: LastEventDisplayProps) => {
  const { data: eventsData } = useGetLastEntriesByChannelQuery(
    {
      channelId,
      page: 1,
      size: 5,
      search: ''
    },
    {
      skip: !channelId,
      pollingInterval: 5000
    }
  )
  const lastEvent = eventsData?.data?.[0]
  const lastEventId = lastEvent?.id

  const { data: photo, isLoading: photoLoading, isError: photoError } = useGetPhotoNumberQuery(
    lastEventId,
    {
      skip: !lastEventId,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false
    }
  )
  const nextImage = photo?.image_base_64
    ? `data:image/jpeg;base64,${photo.image_base_64}`
    : ''
  const lastGoodImageRef = useRef('')
  if (nextImage) {
    lastGoodImageRef.current = nextImage
  } else if (!lastEventId || photoError) {
    lastGoodImageRef.current = ''
  }
  const image = lastGoodImageRef.current
  const imageLoading = photoLoading && !image
  const [zoomOpen, setZoomOpen] = useState(false)
  const handleClick = () => setZoomOpen(true)

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: {
             xs: '16 / 9', 
            //  sm: '21 / 9' 
          },
          overflow: 'hidden',
          bgcolor: '#000',
          backdropFilter: 'blur(10px)',
          cursor: 'pointer',
          '&:hover .zoom-hint': {
            opacity: image ? 1 : 0
          },
          '&:hover img': {
            transform: image ? 'scale(1.03)' : 'none'
          },
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <LastEventImage 
            src={image} 
            loading={imageLoading} 
          />
        </Box>
        { image && <ZoomInTooltipChip/> }
        { channelName && (
          <ChannelNameChip dragHandleProps={dragHandleProps}>
            {channelName}
          </ChannelNameChip>
        ) }
        { lastEvent && <LastEventChip lastEvent={lastEvent} /> }
      </Box>

      {/* Модальное окно — увеличенное изображение */}
      <LastEventZoomInModal
        key={channelId}
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        image={image}
        imageLoading={imageLoading}
        plateNumber={lastEvent?.plate_number}
        channelId={channelId}
      />
    </>
  )
};

const ZoomInTooltipChip = () => (
  <Box
    className="zoom-hint"
    sx={{
      position: 'absolute',
      top: 12,
      right: 12,
      zIndex: 4,
      opacity: 0,
      transition: 'opacity 0.2s ease',
      bgcolor: 'rgba(0,0,0,0.55)',
      borderRadius: 1.5,
      px: 1,
      py: 0.5,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255,255,255,0.15)'
    }}
  >
    <ZoomInIcon sx={{ fontSize: 16, color: '#fff' }} />
    <Typography
      variant="caption"
      sx={{ color: '#fff', fontSize: '0.7rem' }}
    >
      Увеличить
    </Typography>
  </Box>
)

interface ChannelNameChipProps {
  children: string
  dragHandleProps?: ChannelCardDragHandlers
}

const ChannelNameChip = ({ children, dragHandleProps }: ChannelNameChipProps) => {
  const { t } = useTranslation()

  return (
    <Box
      {...dragHandleProps}
      aria-label={t('move_channel')}
      title={t('move_channel')}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      sx={{
        position: 'absolute',
        left: 16,
        top: 12,
        zIndex: 3,
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: 'rgba(0,0,0,0.70)',
        color: '#fff',
        lineHeight: 1.25,
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: dragHandleProps ? 'grab' : 'default',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      { dragHandleProps && <DragIndicatorIcon sx={{ fontSize: 16, color: '#fff' }} /> }
      <CameraAltOutlinedIcon sx={{ fontSize: 16, color: '#fff' }} />
      <Typography variant="caption">
        { children }
      </Typography>
    </Box>
  )
}

const LastEventImage = ({
  src, 
  loading
}) => {
  const { t } = useTranslation();
  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />
  }
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={t('last_event')}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: { xs: 'contain', sm: 'cover' },
          objectPosition: 'center',
          display: 'block',
          transition: 'transform 0.3s ease',
        }}
      />
    )
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 1
      }}
    >
      <ImageIcon
        sx={{ fontSize: 48, opacity: 0.5, color: 'rgba(255,255,255,0.5)' }}
      />
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
        {t('no_image')}
      </Typography>
    </Box>
  )
}

export default LastEventDisplay
